import React, { useState, useEffect } from "react";
import { Box, Button } from "@mui/material";
import useSilentIdTokenRefresh from "../msal/useSilentTokenRefresh";
import { useAppContext } from "../hooks/context/AppContext";
import useLocalStorage from "../hooks/useLocalStorage";
import { useNavigate } from "react-router-dom";
import { useQueryParams } from "../hooks/useQueryParams";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import { useUserApi } from "../Services/api/userApi";
import { useHeader } from "../hooks/context/HeaderContext";

interface HomeProps {
  roleAvailable: boolean;
  userRole: string;
}

const Home: React.FC<HomeProps> = ({ roleAvailable, userRole }) => {
  const { updateHeaderInfo } = useHeader();
  const { getQueryParams, createQueryString } = useQueryParams();
  const navigate = useNavigate();
  const [idToken, setIdToken] = useState(null);
  const [, setAuthData] = useLocalStorage("auth_data", null);
  const { authData, updateAuthData, userData } = useAppContext();
  const { getUser } = useUserApi();
  const [landscapes, setLandscapes] = useState<any[]>([]);
  const [selectedLandscapeId, setSelectedLandscapeId] = useState<string>(
    userData?.LandscapeInfo && userData.LandscapeInfo.length > 0
      ? userData.LandscapeInfo[0].LandscapeId
      : "",
  );
  const [isUserValid, setIsUserValid] = useState(true);
  // --- Get landscapeId from query params if present ---
  const landscapeIdFromQuery = getQueryParams().LandscapeID;
  console.log(userData, "userData");
  // --- Fetch landscapes on mount ----
  useEffect(() => {
    const userInfo = async () => {
      try {
        if (userData.Email) {
          let response = await getUser({
            userEmailId: userData.Email,
            landscapeId: landscapeIdFromQuery ? landscapeIdFromQuery : 0,
          });
          if (response && response[0]?.LandScapeInfo?.length > 0) {
            if (!landscapeIdFromQuery) {
              setLandscapes(response[0].LandScapeInfo);
              setSelectedLandscapeId(response[0].LandScapeInfo[0].LandscapeId);
              setIsUserValid(true);
            } else {
              setSelectedLandscapeId(landscapeIdFromQuery);
              if (
                response[0].LandScapeInfo.find(
                  (l: any) =>
                    String(l.LandscapeId) !== String(landscapeIdFromQuery),
                )
              ) {
                setIsUserValid(false);
              }
            }
            updateHeaderInfo({
              landscapeName: response[0].LandScapeInfo[0].LandscapeName,
            });
          } else {
            setIsUserValid(false);
          }
        }
      } catch (err) {
        console.error("Failed to fetch user details", err);
      }
    };
    userInfo();
  }, [userData]);

  // --- Handler for radio change ---
  const handleLandscapeChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSelectedLandscapeId(event.target.value);

    // Find the selected landscape object
    const selectedLandscape = landscapes.find(
      (l: any) => String(l.LandscapeId) === String(event.target.value),
    );

    // Update header info with the selected landscape name
    if (selectedLandscape) {
      updateHeaderInfo({
        landscapeName: selectedLandscape.LandscapeName,
      });
    }
  };
  // --- Silent token refresh ---
  const getIdTokenSilently = useSilentIdTokenRefresh(
    (token: any | null, account: any | null) => {
      if (token && account) {
        setIdToken(token);
        const updatedAuthData = {
          ...authData,
          idToken: token,
          name: account.name || authData.name,
          username: account.username || authData.username,
          homeAccountId: account.homeAccountId || authData.homeAccountId,
          environment: account.environment || authData.environment,
          tenantId: account.tenantId || authData.tenantId,
          localAccountId: account.localAccountId || authData.localAccountId,
        };

        updateAuthData(updatedAuthData);
        setAuthData(JSON.stringify(updatedAuthData));
      }
    },
  );

  // --- Navigation handler ---
  const handleClick = (path: string, cbtType: string) => {
    let params = getQueryParams();
    // If no landscapeId in query, add the selected one
    if (!landscapeIdFromQuery && selectedLandscapeId) {
      params = { ...params, LandscapeID: selectedLandscapeId };
    }
    params = { ...params, CBTType: cbtType };
    const queryString = createQueryString(params);
    navigate(`${path}?${queryString}`);
  };

  return (
    <>
      <Box>
        <Box
          sx={{
            minHeight: "90vh",
            width: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginTop: "20px",
              marginBottom: "-20px",
              width: "100%",
            }}
          >
            {!roleAvailable ||
              (!isUserValid && (
                <p className="Invalid-Info">
                  You are not an authorized user. Please contact Admin
                </p>
              ))}
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "60vh",
            }}
            // display="content"
            justifyContent="flex-end"
            alignItems="center"
            mt={2}
            mr={2}
            gap={10}
            textAlign={"center"}
          >
            <div className="mandate-actions">
              <Box>
                {!landscapeIdFromQuery && landscapes.length > 0 && (
                  <div className="landscape-section">
                    <FormControl>
                      <FormLabel id="landscape-radio-group-label">
                        Landscape
                      </FormLabel>
                      <RadioGroup
                        row
                        aria-labelledby="landscape-radio-group-label"
                        name="landscape-radio-group"
                        value={selectedLandscapeId}
                        onChange={handleLandscapeChange}
                      >
                        {landscapes.map((landscape: any) => (
                          <FormControlLabel
                            key={landscape.LandscapeId}
                            value={landscape.LandscapeId}
                            control={<Radio />}
                            label={landscape.LandscapeName}
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                  </div>
                )}
              </Box>
              <Box>
                <Button
                  color="primary"
                  variant="contained"
                  disabled={!roleAvailable || !isUserValid}
                  sx={{
                    backgroundColor: "#12239E",
                    borderRadius: "20px",
                    textTransform: "none",
                    fontWeight: "bold",
                    marginRight: "40px",
                    height: "200px",
                    width: "200px",
                    px: 2,
                    "&:hover": { backgroundColor: "#b5b5b5" },
                  }}
                  onClick={() =>
                    handleClick(
                      `${
                        userRole === "PREPARER" || userData.Role === "VIEW"
                          ? "/add-comments"
                          : "/sign-off-page"
                      }`,
                      "BS",
                    )
                  }
                >
                  TBR Comments
                </Button>
              </Box>
            </div>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Home;
